import { useEffect, useState } from "react";
import axios from "axios";
import { Button, CTA, PageHero, Reveal, SectionIntro, useSeo } from "../components/Shared";
import { IMG } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALUES = [
  ["Excellence", "Raise the standard, then make it useful."],
  ["Empathy", "Understand before you advise."],
  ["Ethics", "Build trust through what you do."],
  ["Empowerment", "Leave people more capable."],
  ["Evolution", "Stay in motion, on purpose."],
  ["Education", "Turn knowledge into capability."],
  ["Engagement", "Show up fully, every time."],
  ["Experience", "Make progress feel real."],
];

export default function About() {
  useSeo(
    "About | Dishan International Group",
    "More than a company, an integrated growth ecosystem bridging aspiration and achievement for individuals and organizations."
  );
  const [team, setTeam] = useState([]);
  useEffect(() => {
    axios.get(`${API}/team`).then((r) => setTeam(r.data)).catch(() => {});
  }, []);
  return (
    <>
      <PageHero
        eyebrow="About Dishan"
        titleKey="about.hero.title" title="More than a company. An integrated growth ecosystem."
        subtitleKey="about.hero.subtitle" subtitle="A bridge between aspiration and achievement, helping individuals and organizations thrive in a rapidly evolving world."
      />
      <main>
        <section className="section">
          <div className="container image-split">
            <Reveal className="image-split-media"><img src={IMG.about} alt="Dishan leadership team collaborating" loading="lazy" /></Reveal>
            <Reveal delay={0.1}>
              <span className="eyebrow">Vision / Mission</span>
              <h2>We make the complex clear enough to act on.</h2>
              <p>Our vision is to be the bridge between aspiration and achievement. Our mission is to empower individuals, startups and enterprises through a fusion of knowledge, consulting, collaboration and immersive value creation.</p>
              <Button to="/contact" variant="secondary">Talk to Our Team</Button>
            </Reveal>
          </div>
        </section>
        <section className="section navy">
          <div className="container">
            <SectionIntro eyebrow="Eight principles" title="Values that show up in the work." />
            <div className="values-grid">
              {VALUES.map(([v, copy], i) => (
                <Reveal key={v} delay={(i % 4) * 0.06} className="value">
                  <span>0{i + 1}</span>
                  <h3>{v}</h3>
                  <p>{copy}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        {team.length > 0 && (
          <section className="section mist">
            <div className="container">
              <SectionIntro
                eyebrow="Leadership & team"
                title="The people behind the work."
                copy="A team spanning strategy, brand, education, research and wellbeing."
              />
              <div className="team-grid" data-testid="about-team-section">
                {team.map((m, i) => (
                  <Reveal key={m.id} delay={(i % 3) * 0.07}>
                    <figure className="team-card" data-testid="team-member-card">
                      {m.image_url && <img src={m.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${m.image_url}` : m.image_url} alt={m.name} loading="lazy" />}
                      <figcaption>
                        <strong>{m.name}</strong>
                        <span>{m.role}</span>
                        {m.bio && <p>{m.bio}</p>}
                      </figcaption>
                    </figure>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}
        <section className="section">
          <div className="container">
            <SectionIntro
              eyebrow="Our evolution"
              title="An ecosystem in motion."
              copy="The shape of our work has expanded, while the point of view remains constant: progress should be intelligent, human and built to last."
            />
            <div className="timeline">
              {[
                ["01", "The question", "What if business growth could create more human possibility?"],
                ["02", "The practice", "Consulting, education, brand strategy and wellbeing begin to converge."],
                ["03", "The ecosystem", "A connected platform for people and organizations to move forward."],
              ].map(([n, t, c], i) => (
                <Reveal key={n} delay={i * 0.08} className="timeline-item">
                  <span>{n}</span>
                  <div><h3>{t}</h3><p>{c}</p></div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        <CTA />
      </main>
    </>
  );
}
